<template>
	<DataDialog
		ref="userDialog"
		v-model="dialogUser"
		:title="dialogMode === 'add' ? $t('text.addUser') : $t('text.editUser')"
		:validator="validateUser"
		:reactOn="reactOnState"
		:disableConfirmButton="emailBlocksConfirm || nameFieldsInvalid"
		:onConfirm="handleDialogUpdate"
		width="720px"
	>
		<template #content="{ model }">
			<Alert v-model="errorAlert" type="error" />
			<div class="form-fields">
				<div class="two-col">
					<div>
						<v-label>{{ $t('text.firstName') }} <span class="error-text">({{ $t('text.required') }})</span></v-label>
						<v-text-field
							v-model="model.firstName"
							variant="outlined"
							density="compact"
							:error-messages="firstNameError ? [firstNameError] : []"
							:hide-details="!firstNameError"
							data-cy="firstName"
						/>
					</div>
					<div>
						<v-label>{{ $t('text.lastName') }} <span class="error-text">({{ $t('text.required') }})</span></v-label>
						<v-text-field
							v-model="model.lastName"
							variant="outlined"
							density="compact"
							:error-messages="lastNameError ? [lastNameError] : []"
							:hide-details="!lastNameError"
							data-cy="lastName"
						/>
					</div>
				</div>
				<div>
					<v-label>{{ $t('text.loginEmailAddress') }} <span class="error-text">({{ $t('text.required') }})</span></v-label>
					<v-text-field
						v-model="model.email"
						variant="outlined"
						density="compact"
						:loading="checkingEmail"
						:messages="emailStatusColor !== 'error' ? emailStatusMessage : []"
						:error-messages="emailStatusColor === 'error' ? emailStatusMessage : []"
						:color="emailStatusColor"
						:hide-details="!emailStatusMessage"
						:disabled="isAccepted"
						@input="debouncedCheckEmail"
						data-cy="email"
					/>
				</div>
				<div>
					<v-label>{{ $t('text.preferredLanguage') }} <span class="error-text">({{ $t('text.required') }})</span></v-label>
					<v-select
						v-model="model.language"
						:items="languages"
						item-title="label"
						item-value="value"
						variant="outlined"
						density="compact"
						hide-details
						data-cy="language"
					>
						<template v-slot:item="{ props, item }">
							<v-list-item v-bind="props" class="preferred-language-list-item">
								<div style="display: flex; align-items: center;">
									<LanguageFlag v-model="item.value" />
									<span style="margin-left: 10px;">{{ item.title }}</span>
								</div>
							</v-list-item>
						</template>
						<template v-slot:selection="{ item }">
							<div style="display: flex; align-items: center;">
								<LanguageFlag v-model="item.value" />
								<span style="margin-left: 10px;">{{ item.title }}</span>
							</div>
						</template>
					</v-select>
				</div>
				<div>
					<v-label>{{ $t('text.userRole') }} <span class="error-text">({{ $t('text.required') }})</span></v-label>
					<v-select
						v-model="model.userRole"
						:items="roleOptions"
						item-title="label"
						item-value="value"
						variant="outlined"
						density="compact"
						hide-details
						:disabled="isAccepted"
						data-cy="userRole"
					/>
				</div>
				<div v-if="!isAccepted">
					<p class="helpText">{{ $t('text.userInvitationHelp') }}</p>
				</div>
			</div>
		</template>
	</DataDialog>
</template>

<script>
import DataDialog from '@/components/common/DataDialog.vue'
import LanguageFlag from '@/components/common/LanguageFlag.vue'
import InvitationExecutive from '../../../../../api2/src/modules/trade-hub/InvitationExecutive'
import { InvitationRole } from '../../../../../shared/types/account-types'
import Alert from '@/components/common/Alert.vue'
import { isValidPersonName } from '@/utils/input-validation.js'

export default {
	name: 'AddUserDialog',
	components: { DataDialog, LanguageFlag, Alert },
	props: {
		modelValue: {
			type: Boolean,
			default: false
		},
		mode: {
			type: String,
			default: 'add', // 'add' | 'edit'
			validator: value => ['add', 'edit'].includes(value)
		},
		user: {
			type: Object,
			default: () => ({ firstName: '', lastName: '', email: '', language: 'en', userRole: 'User' })
		},
		serviceProviderId: {
			type: String,
			required: true
		}
	},
	emits: ['update:modelValue', 'user-saved', 'user-updated'],
	data() {
		return {
			dialogUser: { firstName: '', lastName: '', email: '', language: 'en', userRole: 'User' },
			errorAlert: null,
			languages: [
				{ label: 'English', value: 'en' },
				{ label: 'Deutsch', value: 'de' },
				{ label: 'Français', value: 'fr' },
				{ label: 'Italiano', value: 'it' },
				{ label: 'Nederlands', value: 'nl' }
			],
			roleOptions: [
				{ label: 'Admin', value: InvitationRole.ADMIN },
				{ label: 'User', value: InvitationRole.USER },
			],
			// Email validation
			checkingEmail: false,
			emailStatus: null,
			emailCheckTimeout: null,
			// Name field validation - set as a side effect of validateUser (see there for why)
			firstNameError: '',
			lastNameError: '',
		}
	},
	computed: {
		dialogMode() {
			return this.mode
		},
		isAccepted() {
			return this.dialogUser?.status === 'ACCEPTED'
		},
		emailStatusMessage() {
			if (!this.emailStatus) return ''
			return this.emailStatus.message || ''
		},
		emailStatusColor() {
			if (!this.emailStatus) return ''
			if (this.emailStatus.available === false) {
				if (this.emailStatus.status === 'ACCEPTED') return 'error'
				if (this.emailStatus.status === 'SENT') return 'warning'
				return 'error'
			}
			if (this.emailStatus.available === true) return 'success'
			return ''
		},
		emailBlocksConfirm() {
			return !!(this.emailStatus && this.emailStatus.available === false)
		},
		nameFieldsInvalid() {
			return !!(this.firstNameError || this.lastNameError)
		},
		// A computed keeps the same object until email/emailStatus actually change. An
		// inline literal in the template is a new object on every render, which fires
		// DataDialog's deep reactOn watcher (and so validateUser) on every render (MYS-7534).
		reactOnState() {
			return { email: this.dialogUser.email, emailStatus: this.emailStatus }
		}
	},
	watch: {
		user: {
			immediate: true,
			deep: true,
			handler(newUser) {
				this.dialogUser = { ...newUser }
			}
		},
	},
	methods: {
		open() {
			this.errorAlert = null
			this.emailStatus = null // Reset email status
			this.firstNameError = ''
			this.lastNameError = ''
			this.$nextTick(() => this.$refs.userDialog.open())
		},
		validateUser(model) {
			// DataDialog only exposes its live model through this validator (and the
			// #content scoped slot) - not reactively to the rest of this component - so
			// first/last name errors are stashed onto `this` here, to be read both by the
			// inline :error-messages bindings and by nameFieldsInvalid/disableConfirmButton.
			// Each error is computed locally and assigned once: resetting to '' and then
			// re-setting the same message is two real changes, which re-render this component
			// and re-run this validator - an endless loop that froze the tab whenever a name
			// error was showing while the email check completed (MYS-7534).
			if (!model) {
				this.firstNameError = ''
				this.lastNameError = ''
				return this.$t('text.required')
			}

			let firstNameError = ''
			if (!model.firstName?.trim()) {
				firstNameError = this.$t('text.firstNameRequired')
			} else if (!isValidPersonName(model.firstName.trim())) {
				firstNameError = this.$t('text.fieldValPersonNameFormat')
			}
			this.firstNameError = firstNameError

			let lastNameError = ''
			if (!model.lastName?.trim()) {
				lastNameError = this.$t('text.lastNameRequired')
			} else if (!isValidPersonName(model.lastName.trim())) {
				lastNameError = this.$t('text.fieldValPersonNameFormat')
			}
			this.lastNameError = lastNameError

			// Required/format errors reuse the same emailStatus that debouncedCheckEmail
			// populates, so they show inline under the email field (and block confirm via
			// emailBlocksConfirm) exactly like the PEAK-availability case - instead of also
			// returning a footer string here, which would show/block the same thing twice.
			if (!model.email?.trim()) {
				this.setEmailStatus({ available: false, message: this.$t('text.emailRequired') })
			} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(model.email.trim())) {
				this.setEmailStatus({ available: false, message: this.$t('text.validEmailRequired') })
			}
			// else: leave emailStatus as whatever the async availability check last set.

			// Email-availability and name-field blocking are handled via
			// emailBlocksConfirm/nameFieldsInvalid + disableConfirmButton instead of here,
			// so each reason is only ever shown once - inline under its own field - rather
			// than duplicated in the dialog's generic footer error.

			if (!model.language) return this.$t('text.languageRequired')
			if (!model.userRole) return this.$t('text.userRoleRequired')
			return null
		},
		async handleDialogUpdate(model) {
			if (!model) return

			this.errorAlert = null
			try {
				const executive = new InvitationExecutive(this)
				
				if (this.dialogMode === 'add') {
					const inviterUserId = !this.$store.state.isImpersonation ? 
						this.$store.state.loggedInUser.sys.id : 
						this.$store.state.selectedServiceProvider.fields.userAccount.de.sys.id
					
					console.log('Creating invitation with serviceProviderId:', this.serviceProviderId)
					console.log('Request data:', {
						clientId: this.$store.state.selectedClient.sys.id,
						inviterServiceProviderId: this.serviceProviderId,
						inviterUserId,
						firstName: model.firstName,
						lastName: model.lastName,
						email: model.email,
						preferredLocale: this.mapLanguageToLocale(model.language),
						role: model.userRole
					})
						
					await executive.createAndSendInvitation(this.serviceProviderId, {
						clientId: this.$store.state.selectedClient.sys.id,
						inviterServiceProviderId: this.serviceProviderId,
						inviterUserId,
						firstName: model.firstName,
						lastName: model.lastName,
						email: model.email,
						preferredLocale: this.mapLanguageToLocale(model.language),
						role: model.userRole
					})
					this.$emit('user-saved', model)
				} else if (this.dialogMode === 'edit') {
					// Handle edit mode - update existing invitation
					if (model.status === 'ACCEPTED') {
						const response = await executive.updateUser(model.invitationId, {
							invitationId: model.invitationId,
							firstName: model.firstName,
							lastName: model.lastName,
							preferredLocale: this.mapLanguageToLocale(model.language)
						})
						if (!response.success) {
							throw new Error(response.errors?.join(', ') || 'Failed to update user')
						}
					} else {
						await executive.updateInvitation(model.invitationId, {
							invitationId: model.invitationId,
							firstName: model.firstName,
							lastName: model.lastName,
							email: model.email,
							preferredLocale: this.mapLanguageToLocale(model.language),
							role: model.userRole
						})
					}
					this.$emit('user-updated', model)
				}
			} catch (error) {
				console.error('Failed to save user:', error)
				const message = error?.message || String(error || '') || 'Failed to save user'
				this.errorAlert = message
				throw error
			}
		},
		// Email validation methods
		debouncedCheckEmail(event) {
			console.log('debouncedCheckEmail called with:', event, typeof event)
			
			// Clear previous timeout
			if (this.emailCheckTimeout) {
				clearTimeout(this.emailCheckTimeout)
			}

			// Get email value - handle both event object and direct value
			let email = event
			if (event && event.target) {
				email = event.target.value
			}

			// Convert to string and check if empty
			const emailStr = String(email || '').trim()
			console.log('Email string:', emailStr)

			if (!emailStr) {
				// Leave the "required" message validateUser already set for an empty email
				// (both run off the same input event - don't race it back to null here).
				return
			}

			// Set timeout for debounced check
			this.emailCheckTimeout = setTimeout(() => {
				console.log('Calling checkEmailAvailability with:', emailStr)
				this.checkEmailAvailability(emailStr)
			}, 500) // 500ms delay
		},
		async checkEmailAvailability(email) {
			// Basic email validation
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
			if (!emailRegex.test(email)) {
				this.setEmailStatus({
					available: false,
					message: this.$t('text.validEmailRequired')
				})
				return
			}

			this.checkingEmail = true
			try {
				const executive = new InvitationExecutive(this)
				const result = await executive.checkEmailAvailability(email, this.$store.state.selectedClient.sys.id)
				this.setEmailStatus(result)
			} catch (error) {
				console.error('Failed to check email availability:', error)
				this.setEmailStatus({
					available: true,
					message: 'Unable to check email status'
				})
			} finally {
				this.checkingEmail = false
			}
		},
		mapLanguageToLocale(language) {
			// Since we're now using locale codes directly, just return the language
			return language || 'en'
		},
		// Only replaces emailStatus when its content actually changed. emailStatus feeds
		// into the `reactOn` prop watched (deep) by DataDialog, which re-invokes this
		// component's validator on every change - assigning a new-but-equal object here
		// would re-trigger that watcher every render, looping forever (this is what froze
		// the tab on MYS-7293: validateUser set a fresh emailStatus object on every call
		// because the Add-user dialog starts with an empty email).
		setEmailStatus(status) {
			if (this.emailStatus?.available === status?.available && this.emailStatus?.message === status?.message) return
			this.emailStatus = status
		}
	},
	mounted() {
		console.log('serviceProviderId', this.serviceProviderId)
	},
	beforeUnmount() {
		// Clean up timeout
		if (this.emailCheckTimeout) {
			clearTimeout(this.emailCheckTimeout)
		}
	}
}
</script>

<style lang="scss" scoped>
.form-fields {
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.two-col {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 16px;
}

.error-text {
	color: red;
	font-size: 0.875em;
}

.helpText {
	margin-top: 8px;
	color: #666;
	font-size: 0.875em;
	line-height: 1.4;
}

.status-message {
	margin-top: 4px;
	font-size: 0.875em;
	line-height: 1.4;
	
	&.error {
		color: #d32f2f;
	}
	
	&.warning {
		color: #ed6c02;
	}
	
	&.success {
		color: #2e7d32;
	}
}
</style>

<style lang="scss">
.v-list:has(.preferred-language-list-item) {
	padding: 0px;
	.preferred-language-list-item {
		padding-inline: 12px;
		min-height: 60px;
		padding-top: 7px;
		padding-bottom: 7px;
		border-bottom: 1px solid #e5e5e5;

		&:hover {
			cursor: pointer !important;
		}
		.v-list-item__overlay,
		.v-list-item-title,
		.v-list-item__spacer {
			display: none;
		}
		.v-list-item-action {
			margin-inline-end: 0px;
		}
		&.v-list-item--active {
			color: inherit !important;
		}
	}
}
</style>
